import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import { Link } from 'react-router-dom'
import Button from '@mui/material/Button'

import useBilling, { isFreePlan, isPaidPlan } from '../models/useBilling'
import useUsage, { currentPeriod, periodBounds } from '../models/useUsage'
import { formatBytes, formatCents } from '../utilities/general'
import { allowanceBand, allowanceFraction, bandSeverity, exhaustionAt, projectedUsage, shareFraction } from '../utilities/allowance'
import { throttleConsequence, throttleLimitCause } from '../utilities/computeStop'

// Where this node / this network stands against the ORG's bandwidth allowance (app#86).
//
// ⚠⚠ WHY THIS SITS ON A NODE PAGE AT ALL. The enforcement is live while billing is dark, and
// since router#966 the two regimes differ in kind: a PAID org past its allowance is THROTTLED
// (shaped to 1 Mbit/s per node), a FREE org is BLOCKED (transfers refused outright). Both look
// like a network fault from a node page — every agent still online, every file still listed —
// so a user who never opens /usage learns the difference from a support ticket. The number that
// separates "my network is slow" from "my network stopped, and I could have seen it coming"
// belongs beside the traffic that caused it.
//
// ⚠ THE POOL IS AN ORG POOL AND THE MATRIX ABOVE IT IS NOT. The allowance is consumed by every
// node the org owns; this view shows one node or one network. The share line is the bridge, and
// the copy never lets the local number be read as the limit.
//
// ⚠ ONE SAMPLE, NOT A SERIES. `get_org_usage` returns period-to-date totals — one row per axis —
// so there is no trend to draw. The projection is a straight line from that one point and says so.

type Props = {
  /** What the matrix above is showing — 'node' or 'network'. Used in the share sentence. */
  scope: 'node' | 'network'
  /** This node's / network's total bytes for the period (all six legs), or null when unknown. */
  scopeBytes: number | null
}

const formatDay = (ms: number): string =>
  new Date(ms).toLocaleDateString(undefined, { month: 'long', day: 'numeric', timeZone: 'UTC' })

// ⚠ The period's own month, from its START. `periodBounds` end is EXCLUSIVE — the first instant of
// the next month — so a projection dated off it reads "by September 1" for an August month, which
// is a day nobody's August contains. The RESET is correctly that instant and is dated with
// formatDay(end); the projection is about the month, and names the month.
const formatMonth = (ms: number): string =>
  new Date(ms).toLocaleDateString(undefined, { month: 'long', timeZone: 'UTC' })

const pct = (fraction: number): string => `${fraction < 0.01 && fraction > 0 ? '<1' : Math.round(fraction * 100)}%`

export default function TrafficAllowance({ scope, scopeBytes }: Readonly<Props>) {
  const axes = useUsage(state => state.axes)
  const period = useUsage(state => state.period)
  const plan = useBilling(state => state.plan)
  const throttledUntil = useBilling(state => state.throttledUntil)
  const blockedUntil = useBilling(state => state.blockedUntil)
  const status = useBilling(state => state.status)
  const credit = useBilling(state => state.credit)
  const consequences = useBilling(state => state.consequences)

  const bandwidth = axes.find(a => a.axis === 'bandwidth')
  // No axes yet (first paint, or a failed load) — render nothing rather than a fabricated zero.
  if (!bandwidth) return null

  const used = bandwidth.used
  const included = bandwidth.included
  const band = allowanceBand(used, included)
  const fraction = allowanceFraction(used, included)
  const share = shareFraction(scopeBytes, used)

  // ⚠⚠ THREE STATES, NOT A TERNARY (app#141). `isFree` and `isPaid` are BOTH false when the org's
  // plan_id matches no row this reader can see in `plans`, and that state PERSISTS. An
  // `isFree ? … : …` hands it to whichever branch happens to be the else — which is how a Free
  // org came to be told that metered overage applied to it. The unknown case gets its own copy:
  // it still names the standing, and simply declines to say what happens next.
  const isFree = isFreePlan(plan)
  const isPaid = isPaidPlan(plan)

  // Live enforcement. Same reads as the app-wide banner and the Usage panel — one source, so a
  // node page cannot say "throttled" while the banner says "stopped".
  // ⚠⚠ THE SENTENCE TOO, NOT ONLY THE STAMPS (app#323). The throttled line was a literal that said
  // "not an outage" for every cause, so a lapsed org read on this page that its machines were fine
  // while the banner said they were stopped. It now derives the cause with `throttleLimitCause` and
  // renders `throttleConsequence`, as `Usage` does. `credit` is loaded by the banner, which is
  // mounted on every page; a Member never has it, and falls to the same `cap` arm there too.
  // ⚠ Blocked OUTRANKS throttled and suppresses it, for the reason BillingBanner spells out: two
  // bandwidth verdicts at once is how a reader decides neither is real.
  const blocked = !!blockedUntil && new Date(blockedUntil).getTime() > Date.now()
  const throttled = !blocked && !!throttledUntil && new Date(throttledUntil).getTime() > Date.now()

  const { start, end } = periodBounds(period)
  const startMs = new Date(start).getTime()
  const endMs = new Date(end).getTime()
  const isCurrent = period === currentPeriod()
  const projected = isCurrent ? projectedUsage(used, startMs, endMs, Date.now()) : null
  const runsOutAt = isCurrent ? exhaustionAt(used, included, startMs, endMs, Date.now()) : null

  const severity = bandSeverity(band)
  const overBytes = bandwidth.overage ?? 0

  // What happens at the limit, in this org's regime. Free stops, paid slows and meters, unknown
  // says neither. Present tense once it has happened, future tense while it is a forecast.
  let consequence: string | null = null
  if (blocked) {
    consequence = 'Transfers are stopped right now. Your devices stay online and your files stay in place.'
  } else if (throttled) {
    consequence = `Every node is limited to 1 Mbps right now. ${throttleConsequence(throttleLimitCause(status, isPaid, isFree, credit), consequences)}`
  } else if (band === 'over' && isFree) {
    consequence = `Allowance used up — transfers stop until ${formatDay(endMs)}, or sooner on a paid plan.`
  } else if (band === 'over' && isPaid) {
    consequence = `${formatBytes(overBytes)} over — ${formatCents(bandwidth.overageCents)} of metered traffic this period.`
  } else if (band === 'over') {
    consequence = `${formatBytes(overBytes)} over the included allowance.`
  } else if (runsOutAt && isFree) {
    consequence = `At this rate it runs out around ${formatDay(runsOutAt)} — transfers stop on the free plan.`
  } else if (runsOutAt && isPaid) {
    consequence = `At this rate it runs out around ${formatDay(runsOutAt)} — traffic past it is metered.`
  } else if (runsOutAt) {
    consequence = `At this rate it runs out around ${formatDay(runsOutAt)}.`
  } else if (projected != null && band !== 'unlimited') {
    consequence = `On track for about ${formatBytes(projected)} by the end of ${formatMonth(startMs)}.`
  }

  return (
    <Box sx={{ mt: 1, maxWidth: 420 }}>
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Tooltip
          arrow
          title="Bandwidth included with your plan, shared by every node in this organization. All the traffic in the table above counts toward it."
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Organization bandwidth
          </Typography>
        </Tooltip>
        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {band === 'unlimited'
            ? `${formatBytes(used)} used · unlimited`
            : `${formatBytes(used)} / ${formatBytes(included ?? 0)}${fraction == null ? '' : ` · ${pct(fraction)}`}`}
        </Typography>
      </Stack>
      {/* ⚠ The BAR is clamped at 100%; the NUMBERS above are not. A bar cannot render 240% and a
          reader deserves to know they are at 240%. */}
      <LinearProgress
        variant="determinate"
        value={fraction == null ? 0 : Math.min(100, fraction * 100)}
        color={severity}
        sx={{ my: 0.5 }}
      />
      {share != null && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          This {scope} used {formatBytes(scopeBytes ?? 0)} of it — {pct(share)} of the organization total.
        </Typography>
      )}
      {consequence && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: severity === 'success' ? 'text.secondary' : `${severity}.main` }}
        >
          {consequence}
        </Typography>
      )}
      {/* One way on, and it is /usage rather than /billing: the reader is looking at traffic, and
          the allowance detail (every axis, the projected bill, the per-network hotspots) is what
          the next question needs. /billing is one click on from there, role-safe. */}
      <Button component={Link} to="/usage" size="small" sx={{ mt: 0.5, ml: -1 }}>
        Usage detail
      </Button>
    </Box>
  )
}
